import { BarChart3 } from "lucide-react";
import { money } from "@/lib/format";

/* ------------------------------------------------------------------ */
/* what's really closing                                               */
/* ------------------------------------------------------------------ */

/** Bar colour: grey for unrecorded, green for wins, amber for losses. */
const barColour = (row, isWon) => {
  if (!row.key) return "bg-muted-foreground/30";
  return isWon ? "bg-emerald-500" : "bg-[color:var(--tt-warn-border)]";
};

export const OutcomeBreakdown = ({ outcomes }) => {
  const groups = ["won", "lost"].map((key) => outcomes?.[key]).filter((g) => g && g.total);
  if (!groups.length) return null;

  return (
    <section
      data-testid="outcome-breakdown"
      className="mt-5 rounded-xl border border-border bg-white"
    >
      <header className="border-b border-border bg-[color:var(--tt-surface-2)] px-4 py-3">
        <h4 className="flex items-center gap-2 text-sm font-bold text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
          What's really closing
        </h4>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Only counts leads where someone recorded a reason. The more you record, the more this
          tells you.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
        {groups.map((group) => {
          const biggest = Math.max(...group.rows.map((r) => r.count), 1);
          const isWon = group.stage === "won";
          return (
            <div key={group.stage} data-testid={`outcome-breakdown-${group.stage}`}>
              <div className="flex items-baseline justify-between gap-2">
                <h5 className="text-[13px] font-bold text-foreground">{group.label}</h5>
                <span className="font-amount text-[11px] text-muted-foreground">
                  {group.recorded} of {group.total} recorded
                </span>
              </div>
              <ul className="mt-2.5 space-y-2">
                {group.rows.map((row) => (
                  <li key={row.key || "unrecorded"}>
                    <div className="flex items-baseline justify-between gap-2 text-[13px]">
                      <span
                        className={
                          row.key ? "text-foreground" : "italic text-muted-foreground"
                        }
                      >
                        {row.label}
                      </span>
                      <span className="font-amount whitespace-nowrap text-xs text-muted-foreground">
                        <strong className="text-foreground">{row.count}</strong>
                        {row.value ? ` · ${money(row.value)}` : ""}
                      </span>
                    </div>
                    <div
                      className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
                      role="presentation"
                    >
                      <div
                        className={`h-full rounded-full ${barColour(row, isWon)}`}
                        style={{ width: `${Math.round((row.count / biggest) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};
